// src/resources/reports/ReportCustomerRecovery.tsx
//
// Relatório "Recuperação de clientes" (plano Task 10, contrato
// `GET /chains/{chainId}/reports/customer-recovery`, spec §8.4). Segue o mesmo
// molde do ReportDiscount.jsx: `createReportSummaryResponse` embrulha
// { data, summaryData, id } dentro de data[0] — por isso a leitura manual de
// `props.data[0]` em vez de deixar o Datagrid ler `data` direto.
//
// O "Total do período" usa os mesmos cartões da aba Resultado da campanha (ResultCards):
// o backend manda o resumo com os mesmos campos do `campaignMetrics`.
import React from 'react';
import moment from 'moment';
import {
  AutocompleteInput, Datagrid, DateField, Filter, FunctionField,
  ReferenceInput, useListContext, usePermissions, useRecordContext,
} from 'react-admin';
import { Box, Chip, Grid, Typography } from '@mui/material';
import { Permission, ReportList } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from '../../components/ListPagination';
import { PERMISSIONS } from '../../constants';
import { getChainId } from '../../lib';
import { filterToQuery } from '../../utils';
import { __t } from '../../i18n/t';
import { campaignSituationColor, campaignSituationLabel } from '../customerRecoveryRules/components/labels';
import { ResultCards, count, money, pct, times } from '../customerRecoveryRules/components/ResultTab';

const titleSx = { margin: '10px 0 10px 0', textAlign: 'center', fontWeight: 'bold' } as const;

const date = (iso?: string | null) => (iso ? moment(iso).format('DD/MM/YYYY') : null);

/** Número em cima, explicação curta embaixo (ex.: "694" / "7,7% do público"). */
const Cell: React.FC<{ value: React.ReactNode; detail?: React.ReactNode }> = ({ value, detail }) => (
  <Box>
    <Typography variant="body2">{value}</Typography>
    {detail && <Typography variant="caption" color="text.secondary" component="div">{detail}</Typography>}
  </Box>
);

const levelPeriod = (level: any) => {
  if (!level.startedAt) return __t('Não começou');
  return `${date(level.startedAt)} → ${level.endedAt ? date(level.endedAt) : __t('em andamento')}`;
};

// O react-admin 5 não passa `record` por prop pro `expand`: vem do contexto. Lendo da prop, o painel
// ficava sempre vazio ("Nenhum resultado encontrado / Limpar filtros").
const LevelsPanel: React.FC = () => {
  const record = useRecordContext();
  const levels = record?.levels || [];
  return (
    <Datagrid
      data={levels}
      total={levels.length}
      bulkActionButtons={false}
      rowClick={false}
      empty={<Typography variant="body2" color="text.secondary" p={1}>Campanha sem níveis.</Typography>}
      sx={{ '& .RaDatagrid-headerCell': { fontWeight: 700 } }}
    >
      <FunctionField label="Nível" render={(l: any) => `${l.levelOrder}. ${l.name}`} />
      <FunctionField
        label="Desconto"
        render={(l: any) => (l.discount ? l.discount.description || l.discount.identify : __t('Sem desconto'))}
      />
      <FunctionField label="Período" render={levelPeriod} />
      <FunctionField label="Duração" render={(l: any) => (l.durationDays ? __t('%{n} dias', { n: l.durationDays }) : '—')} />
      <FunctionField label="Voltaram" textAlign="right" render={(l: any) => count(l.recoveredCount)} />
      <FunctionField label="Com cupom" textAlign="right" render={(l: any) => count(l.broughtCount)} />
      <FunctionField label="Custo" textAlign="right" render={(l: any) => money(l.cost)} />
    </Datagrid>
  );
};

// FunctionField (não um componente à parte): o Datagrid lê o header do prop `label`
// do filho direto — um componente próprio sem esse prop fica sem título de coluna
// (mesmo padrão de SituationField em CustomerRecoveryRuleList.tsx).
const SituationField: React.FC = () => (
  <FunctionField
    label="Situação"
    render={(record: any) => <Chip label={campaignSituationLabel(record)} color={campaignSituationColor(record) as any} size="small" />}
  />
);

const CustomerRecoveryReportList: React.FC = () => {
  const props = useListContext();
  const { permissions } = usePermissions();
  // A tela da campanha pede a permissão da campanha, não a do relatório.
  const canOpenCampaign = !!permissions?.some((p: string) => p === PERMISSIONS.ADMIN || p === PERMISSIONS.CUSTOMER_RECOVERY_RULES);

  let rows: any[] = [];
  let summary: any = null;

  if (props.data) {
    const reportInfo = (props.data as any)[0];
    if (reportInfo) {
      rows = reportInfo.data || [];
      summary = reportInfo.summaryData || null;
    }
  }

  return (
    <>
      <Typography sx={titleSx}>
        {summary?.campaigns > 1
          ? __t('Total do período (%{n} campanhas)', { n: summary.campaigns })
          : __t('Total do período')}
      </Typography>
      {summary && (
        <Grid container spacing={2} sx={{ px: 2, mb: 6 }}>
          <ResultCards m={summary} />
        </Grid>
      )}

      <Typography sx={titleSx}>Campanhas</Typography>
      <Datagrid
        data={rows}
        bulkActionButtons={false}
        rowClick={canOpenCampaign ? (id: any) => `/customer-recovery-rules/${id}/show` : false}
        expand={<LevelsPanel />}
      >
        <FunctionField
          label="Campanha"
          render={(r: any) => (
            <Cell
              value={r.name}
              detail={r.currentLevelName
                ? __t('%{level} · nível %{order} de %{total}', { level: r.currentLevelName, order: r.currentLevelOrder, total: r.levelsCount })
                : null}
            />
          )}
        />
        <SituationField />
        <FunctionField
          label="Período"
          render={(r: any) => (
            <Cell
              value={`${date(r.startedAt) || '—'} → ${date(r.finishedAt || r.endsAt) || '—'}`}
              detail={!r.finishedAt && r.endsAt ? __t('fim previsto') : null}
            />
          )}
        />
        <FunctionField label="Público" textAlign="right" render={(r: any) => count(r.audienceSize)} />
        <FunctionField
          label="Voltaram"
          textAlign="right"
          render={(r: any) => <Cell value={count(r.recoveredCount)} detail={__t('%{rate} do público', { rate: pct(r.metrics?.recoveryRate) })} />}
        />
        <FunctionField
          label="Com cupom"
          textAlign="right"
          render={(r: any) => <Cell value={count(r.broughtCount)} detail={__t('%{n} sem cupom', { n: count(r.metrics?.returnedWithoutCoupon) })} />}
        />
        <FunctionField
          label="Custo"
          textAlign="right"
          render={(r: any) => <Cell value={money(r.totalCost)} detail={__t('%{value} por trazido', { value: money(r.metrics?.costPerBrought) })} />}
        />
        <FunctionField label="Receita com cupom" textAlign="right" render={(r: any) => money(r.metrics?.broughtRevenue)} />
        <FunctionField label="Retorno por R$" textAlign="right" render={(r: any) => times(r.metrics?.revenuePerReal)} />
        <FunctionField label="Receita da volta" textAlign="right" render={(r: any) => money(r.metrics?.returnRevenue)} />
        <FunctionField
          label="30 dias seguintes"
          textAlign="right"
          render={(r: any) => (r.metrics?.revenue30d != null ? money(r.metrics.revenue30d) : '—')}
        />
      </Datagrid>
    </>
  );
};

const FilterTablePrint: React.FC = () => {
  const { filterValues } = useListContext();
  return (
    <Datagrid data={[{ ...filterValues, generatedReportAt: moment().toISOString() }]} bulkActionButtons={false} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <DateField source="generatedReportAt" label="Gerado às" showTime sortable={false} locales="pt-BR" />
    </Datagrid>
  );
};

const CustomerRecoveryReportFilter: React.FC<any> = (props) => (
  <Filter {...props}>
    <BetweenDatesInput alwaysOn />
    <ReferenceInput
      source="ruleId"
      reference="customer-recovery-rules"
      filter={{ chainId: getChainId() }}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={null as unknown as number}
    >
      <AutocompleteInput
        filterToQuery={filterToQuery}
        label="Campanha"
        emptyText="Todas"
        optionText="name"
        sx={{ minWidth: 220 }}
      />
    </ReferenceInput>
  </Filter>
);

const customerRecoveryReportExporter = async (data: any[]) => {
  const reportInfo = data?.[0] as any;
  const rows = reportInfo?.data || [];
  const decimal = (v: any) => (v == null ? '' : Number(v).toFixed(2));
  return rows.map((r: any) => ({
    nome: r.name,
    situacao: campaignSituationLabel(r),
    nivelAtual: r.currentLevelName || '—',
    inicio: r.startedAt ? moment(r.startedAt).format('DD/MM/YYYY HH:mm') : '—',
    fim: r.finishedAt ? moment(r.finishedAt).format('DD/MM/YYYY HH:mm') : '—',
    publico: r.audienceSize ?? 0,
    voltaram: r.recoveredCount ?? 0,
    taxaDeVolta: r.metrics?.recoveryRate != null ? (r.metrics.recoveryRate * 100).toFixed(1) : '',
    comCupom: r.broughtCount ?? 0,
    semCupom: r.metrics?.returnedWithoutCoupon ?? 0,
    naoVoltaram: r.metrics?.expired ?? 0,
    custo: decimal(r.totalCost),
    custoPorTrazido: decimal(r.metrics?.costPerBrought),
    receitaComCupom: decimal(r.metrics?.broughtRevenue),
    retornoPorReal: decimal(r.metrics?.revenuePerReal),
    receitaDaVolta: decimal(r.metrics?.returnRevenue),
    receita30Dias: decimal(r.metrics?.revenue30d),
  }));
};

const ReportCustomerRecovery: React.FC<any> = (props) => (
  <Permission permission={PERMISSIONS.CUSTOMER_RECOVERY_REPORT}>
    <Box>
      <ReportList periodRequired
        {...props}
        filters={<CustomerRecoveryReportFilter />}
        perPage={25}
        sort={{ field: 'startedAt', order: 'DESC' }}
        filterDefaultValues={{
          chainId: getChainId(),
          from: moment().startOf('day').subtract(1, 'month').toISOString(),
          to: moment().endOf('day').toISOString(),
        }}
        resource="chains/chainId/reports/customer-recovery"
        title="Recuperação de Clientes"
        titleOnPrint="Relatório de Recuperação de Clientes"
        filterTablePrint={<FilterTablePrint />}
        pagination={<ListPagination />}
        customExporter={customerRecoveryReportExporter}
      >
        <CustomerRecoveryReportList />
      </ReportList>
    </Box>
  </Permission>
);

export default ReportCustomerRecovery;
